import { Resolver, Mutation, Arg, Ctx, Directive } from 'type-graphql'

import { getRedirectSubdomainPath, getRepository } from '@things-factory/shell'
import { BoardTemplate } from './board-template.js'
import { Board } from '../board/board.js'
import { NewBoardTemplate, BoardTemplatePatch } from './board-template-type.js'
import { thumbnail } from '../../controllers/thumbnail.js'

@Resolver(BoardTemplate)
export class BoardTemplateMutation {
  @Directive('@transaction')
  @Directive('@privilege(category: "board-template", privilege: "mutation", domainOwnerGranted: true)')
  @Mutation(returns => BoardTemplate, { description: 'To create new BoardTemplate' })
  async createBoardTemplate(
    @Arg('boardTemplate') boardTemplate: NewBoardTemplate,
    @Ctx() context: ResolverContext
  ): Promise<BoardTemplate> {
    const { domain, user, tx } = context.state
    const repository = getRepository(BoardTemplate, tx)

    const oldBoardTemplate: BoardTemplate = await repository.findOneBy({
      name: boardTemplate.name,
      domain: { id: domain.id }
    })

    if (oldBoardTemplate) {
      throw new Error(context.t('error.board-template name is already taken', { name: boardTemplate.name }))
    }

    const newBoardTemplate: Partial<BoardTemplate> = {
      ...boardTemplate
    }

    const base64 = await thumbnail({
      model: boardTemplate.model,
      context
    })

    if (base64 && base64 !== 'null') {
      newBoardTemplate.thumbnail = 'data:image/png;base64,' + base64
    } else {
      newBoardTemplate.thumbnail = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
    }

    return await repository.save({
      domain,
      ...newBoardTemplate,
      creator: user,
      updater: user
    })
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "board-template", privilege: "mutation", domainOwnerGranted: true)')
  @Mutation(returns => BoardTemplate, { description: 'To modify BoardTemplate information' })
  async updateBoardTemplate(
    @Arg('id') id: string,
    @Arg('patch') patch: BoardTemplatePatch,
    @Ctx() context: ResolverContext
  ): Promise<BoardTemplate> {
    const { domain, user, tx } = context.state
    const repository = getRepository(BoardTemplate, tx)

    const boardTemplate = await repository.findOne({
      where: { domain: { id: domain.id }, id }
    })

    if (patch.model) {
      const base64 = await thumbnail({
        model: patch.model,
        context
      })

      if (base64 && base64 !== 'null') {
        patch.thumbnail = 'data:image/png;base64,' + base64
      } else {
        patch.thumbnail = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
      }
    }

    return await repository.save({
      ...boardTemplate,
      ...patch,
      updater: user
    })
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "board-template", privilege: "mutation", domainOwnerGranted: true)')
  @Mutation(returns => Boolean, { description: 'To delete BoardTemplate' })
  async deleteBoardTemplate(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<boolean> {
    const { domain, tx } = context.state

    await getRepository(BoardTemplate, tx).delete({ domain: { id: domain.id }, id })

    return true
  }

  @Directive('@transaction')
  @Directive('@privilege(category: "board-template", privilege: "mutation", domainOwnerGranted: true)')
  @Mutation(returns => BoardTemplate, { description: 'To register a board as a board template with the given ID' })
  async registerBoardAsTemplate(
    @Arg('id', { description: 'board Id to be regiestered' }) id: string,
    @Arg('name', { description: 'name of board template to be regiestered' }) name: string,
    @Arg('description', { description: 'description of board template to be regiestered' }) description: string,
    @Arg('visibility', { description: 'visibility of board template to be regiestered' }) visibility: string,
    @Ctx() context: ResolverContext
  ): Promise<BoardTemplate> {
    const { domain, user, notify, tx } = context.state

    const boardTemplateRepository = getRepository(BoardTemplate, tx)

    const board = await getRepository(Board, tx).findOne({
      where: { domain: { id: domain.id }, id }
    })

    if (!board) {
      throw new Error(`Board given id(${id}) is not found`)
    }

    name ||= board.name

    const { model, thumbnail } = board

    const boardTemplate = await boardTemplateRepository.findOne({
      where: { domain: { id: domain.id }, name }
    })

    const registered = boardTemplate
      ? await boardTemplateRepository.save({
          ...boardTemplate,
          name,
          description: description || boardTemplate.description,
          visibility: visibility || boardTemplate.visibility,
          model,
          thumbnail,
          updater: user
        })
      : await boardTemplateRepository.save({
          domain: { id: domain.id },
          name,
          description: description || board.description,
          model,
          thumbnail,
          visibility: visibility || 'private',
          updater: user,
          creator: user
        })

    notify &&
      notify({
        mode: 'in-app',
        title: `BoardTemplate '${registered.name}' registered`,
        body: `BoardTemplate '${registered.name}' registered by ${user.name}\n${registered.description}`,
        image: getRedirectSubdomainPath(context, domain, `/board-template-thumbnail/${registered.id}`),
        url: getRedirectSubdomainPath(context, domain, `/board-template-viewer/${registered.id}`)
      })

    return registered
  }
}
